import { useEffect, useState } from "react";
import Form from "@/core/components/Form";
import { Loading } from "@/core/components/Loading/Loading";
import { useClaimCheck } from "@/core/hooks/useClaimCheck/useClaimCheck";
import { useAnalyticsPageView } from "@/core/hooks/useAnalyticsPageView/useAnalyticsPageView";
import { Theme } from "@mui/system";
import { Box, Container } from "@mui/material";
import { InitialisationConfig } from "@/core/utils/getInitialisationConfig";
import { beforeSubmit } from "./beforeSubmit";
import { ActionProperties, actions } from "@/core/components/Action/actions";
import { MaterialColors } from "@/core/types/types";
import ProgressBarWithText from "@/core/components/ProgressBar/ProgressBarWithText";
import useCustomStyles from "@/core/hooks/useCustomStyles/useCustomStyles";
import { useGetPreviousAnswers } from "@/core/hooks/useGetPreviousAnswers/useGetPreviousAnswers";

interface ClaimCheckProps {
  initConfig?: InitialisationConfig;
}

const styles = (theme: Theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "90vw",
    [theme.breakpoints.up("md")]: {
      width: "50vw",
    },
  },
});

const PARTNER_TOKEN_VALIDATION_VARIABLES: string = "partnerToken,tokenError";

const ClaimCheck = ({ initConfig }: ClaimCheckProps) => {
  const classes: any = useCustomStyles(styles);
  const {
    currentStep,
    initialise,
    submitStep,
    executeAction,
    flush,
    syncSignature,
    isLoading,
    error,
  } = useClaimCheck();
  const { result, loading } = useGetPreviousAnswers(
    PARTNER_TOKEN_VALIDATION_VARIABLES,
    { dataIsRequired: false }
  );
  const [instanceProcessed, setInstanceProcessed] = useState<boolean>(false);

  useAnalyticsPageView(currentStep);

  const isInstanceValid = (
    variables: any,
    newPartnerToken: string | undefined
  ) => {
    const { partnerToken: storedPartnerToken, tokenError } = variables;
    return !(
      tokenError &&
      storedPartnerToken &&
      newPartnerToken &&
      storedPartnerToken !== newPartnerToken
    );
  };

  useEffect(() => {
    if (result && !loading && !instanceProcessed) {
      const actionProps = initConfig as ActionProperties;
      const {
        action: actionName,
        processId,
        utm_campaign,
        syncSignature: shouldSyncSignature,
      } = actionProps;
      const action = actions[actionName];

      if (action) {
        executeAction(action, processId, utm_campaign);
      } else {
        const intializeInstance = () => {
          const newPartnerToken = (initConfig as any).query.partnerToken;
          const isValid = isInstanceValid(result, newPartnerToken);

          if (isValid) {
            initialise(initConfig);
          } else {
            flush().then(() =>
              initialise({
                ...initConfig,
                partnerToken: newPartnerToken,
              } as InitialisationConfig)
            );
          }
        };

        if (shouldSyncSignature) {
          syncSignature(processId).then(() => intializeInstance());
        } else {
          intializeInstance();
        }
      }

      setInstanceProcessed(true);
    }
  }, [result, loading]);

  if (error) {
    throw error;
  }

  if (!currentStep) {
    return (
      <Box sx={{ marginTop: "70px" }}>
        <Loading />
      </Box>
    );
  }

  const onSubmit = async (formAnswers: any) => {
    const newFormAnswers = await beforeSubmit(currentStep, formAnswers);
    await submitStep(newFormAnswers);
  };

  const {
    components,
    extensionProperties: {
      progressPercentage,
      progressText,
      progressTextColor = MaterialColors.Primary,
    },
  } = currentStep;

  return (
    <Container sx={classes?.root}>
      {isLoading ? (
        <Box sx={{ display: "flex", alignSelf: "center", marginTop: "70px" }}>
          <Loading />
        </Box>
      ) : (
        <>
          <ProgressBarWithText
            progressText={progressText}
            progressTextColor={progressTextColor}
            progressPercentage={progressPercentage}
          />
          <Form formComponents={components} onSubmit={onSubmit} />
        </>
      )}
    </Container>
  );
};

export default ClaimCheck;
